import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';

import { App } from '@capacitor/app';
import { Device } from '@capacitor/device'; 
import { Config } from 'src/app/app-constant';

import { from, Observable, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { HttpService } from 'src/app/service/http/http.service';

type platforms = 'android' | 'ios' | 'web'; 

@Injectable({
  providedIn: 'root'
})

export class AppUpdateServiceService {
  
  constructor( private httpService: HttpService, private platform: Platform, private config: Config) {}
  
  // Get current Device Information
  async getDeviceInfo() {
    return await Device.getInfo();
  }

  async getDeviceId(){
    return await Device.getId();
  }
  
  // Check if device is  web browser or mobile( android or ios )
  getPlatform(): platforms {
    return this.platform.is('ios') ? 'ios' : this.platform.is('android') ? 'android' : 'web';
  }
  
  // Get App Version if its an app or will return web
  getCurrentVersion(): Observable<string> {
    return from(App.getInfo()).pipe(
      map(info => info.version),
      catchError(err => {
        console.warn('Could not fetch app version:', err);
        return of('web');
      })
    );
  }
  
  
  getVersionInfo() {
    return this.httpService.callGetServiceWithoutToken(this.config.getOrUpdateAppVersionsDetails).pipe(
      map((res: any) => {
        // Success Response
        if (res && res.status && res.data) {
          return {
            latestVersionIos: res.data.latestVersionIos,
            latestVersionAndroid: res.data.latestVersionAndroid,
            forceUpdate: res.data.forceUpdate,
            downloadUrlIos: res.data.downloadUrlIos,
            downloadUrlAndroid: res.data.downloadUrlAndroid
          };
        }
        // Fail Response
        return {
          latestVersionIos: '0.0.0',
          latestVersionAndroid: '0.0.0',
          forceUpdate: false,
          downloadUrlIos: '',
          downloadUrlAndroid: ''
        };
      }),
      catchError( err => {
        console.error('Failed to fetch version info', err);
        // Return defaults so app doesn't crash
        return of(
          {
            latestVersionIos: '0.0.0',
            latestVersionAndroid: '0.0.0',
            forceUpdate: false,
            downloadUrlIos: '',
            downloadUrlAndroid: ''
          }
        );
      })
    );
  }
  
  
  private compareVersions(v1: string, v2: string): number {
    if (!/^\d+(\.\d+)*$/.test(v1) || !/^\d+(\.\d+)*$/.test(v2)) {
      console.error('Invalid version format:', v1, v2);
      return -1;
    }
    const p1 = v1.split('.').map(n => parseInt(n, 10));
    const p2 = v2.split('.').map(n => parseInt(n, 10));
    
    for (let i = 0; i < Math.max(p1.length, p2.length); i++) {
      const num1 = p1[i] || 0;
      const num2 = p2[i] || 0;
      if (num1 > num2) return 1;
      if (num1 < num2) return -1;
    }
    return 0;
  }
  
  
  isUpdateAvailable(): Observable<{ available: boolean, forceUpdate: boolean, downloadUrl: string }> {
    const platform = this.getPlatform();
    
    return this.getCurrentVersion().pipe(
      switchMap( currentVersion =>
        this.getVersionInfo().pipe(
          map( (info:any) => {            
            const updatedVersion = platform === 'ios' ? info.latestVersionIos : platform === 'android' ? info.latestVersionAndroid : 'web';
            const downloadUrl = platform === 'ios' ? info.downloadUrlIos : platform === 'android' ? info.downloadUrlAndroid : '';
            const updateAvailable = updatedVersion === 'web' ? false : this.compareVersions(updatedVersion, currentVersion) > 0;
            
            // console.log(updatedVersion, currentVersion, updateAvailable);
            
            return { available: updateAvailable, forceUpdate: info.forceUpdate, downloadUrl };
          })
        )
      )
    );
  }
  
  getUpdateAppVersionsDetails(){
    return this.httpService.callGetServiceWithoutToken(this.config.getOrUpdateAppVersionsDetails);
  }

  updateAppVersionsDetails(formData: any): Observable<any>{
    return this.httpService.callPutService(this.config.getOrUpdateAppVersionsDetails, formData);
  }

}
